import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import { getSparepartList } from "api/inventorySpareparts";
import { searchBoxStyles } from "globalStyles";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import ListingImageCol from './ListingImageCol';
import MDBox from "./MDBox";
import MDButton from "./MDButton";
import MDTypography from "./MDTypography";
import SparePartTagSelector from "./SparePartTagSelector";

const SparePartsMultiSelectModal = ({ open, onClose, value, onChange, rideId }) => {
    const [spareParts, setSpareParts] = useState([]);
    const [selectedParts, setSelectedParts] = useState([]);
    const [selectedTag, setSelectedTag] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (open) {
            console.log("SparePartsMultiSelectModal opened with value:", value);
            if (Array.isArray(value)) {
                const partsWithQty = value.map(part => ({
                    id: part.id,
                    name: part.name,
                    qty: part.qty || 0,
                    stock: part.stock
                }));
                setSelectedParts(partsWithQty);
            } else {
                setSelectedParts([]);
            }
            fetchData();
        }
        // eslint-disable-next-line
    }, [open]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const filterParams = {
                ...(searchQuery ? { search: searchQuery } : {}),
                ...(selectedTag ? { tag: selectedTag } : {}),
                ...(rideId ? { ride_id: rideId } : {}),
                page: 1,
                per_page: 100
            };
            const res = await getSparepartList(filterParams);
            setSpareParts(res.data?.data || []);
        } catch (error) {
            console.error('Error fetching spare parts:', error);
        } finally {
            setIsLoading(false);
        }
    }, [searchQuery, selectedTag, rideId]);

    useEffect(() => {
        if (open) {
            const timeoutId = setTimeout(() => {
                fetchData();
            }, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [searchQuery, selectedTag]);




    const handleSelect = useCallback((item) => {
        const { id, name, stock } = item;
        setSelectedParts((prev) => {
            const existingPart = prev.find(sp => sp.id === id);
            if (existingPart) {
                return prev.filter(sp => sp.id !== id);
            } else {
                const originalPart = Array.isArray(value) ? value.find(v => v.id === id) : null;
                return [...prev, {
                    id,
                    name,
                    qty: originalPart?.qty || 0,
                    stock: stock
                }];
            }
        });
    }, [value]);

    const handleRemoveChip = useCallback((id) => {
        setSelectedParts((prev) => prev.filter((sp) => sp.id !== id));
    }, []);

    const handleConfirm = () => {
        if (onChange) {
            onChange(selectedParts);
        }
        onClose();
    };



    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                Select Spare Parts
                <IconButton aria-label="close" onClick={onClose} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <MDBox px={2}>
                {selectedParts.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {selectedParts.map((sp) => (
                            <Chip
                                key={sp.id}
                                label={sp.name}
                                onDelete={() => handleRemoveChip(sp.id)}
                            />
                        ))}
                    </Box>
                )}
            </MDBox>

            <MDBox px={2} pb={2}>
                <MDBox sx={{ display: 'flex', gap: 2, my: 2 }}>
                    <MDBox
                        component="input"
                        placeholder="Search spare parts..."
                        sx={{
                            flex: 1,
                            ...searchBoxStyles
                        }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <MDBox sx={{ flex: 1, minWidth: 150 }}>
                        <SparePartTagSelector
                            label="Tag"
                            value={selectedTag}
                            onChange={setSelectedTag}
                            isFilter
                        />
                    </MDBox>
                </MDBox>

                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                        <TableHead sx={{ display: "table-header-group" }}>
                            <TableRow>
                                <TableCell width={40} align="center"></TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Tag</TableCell>
                                <TableCell>Stock</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : spareParts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <MDTypography variant="body2" color="text">
                                            No spare parts found
                                        </MDTypography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                spareParts.map((item) => {
                                    const isSelected = selectedParts.some(sp => sp.id === item.id);
                                    return (
                                        <TableRow
                                            key={item.id}
                                            sx={{
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleSelect(item)}
                                        >
                                            <TableCell align="center" >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleSelect(item)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <ListingImageCol
                                                    imgUrl={item.img_filename ?? item.images?.[0]?.url}
                                                    name={item.name}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="body2">{item.tag}</MDTypography>
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="body2">{item.stock ?? 0}</MDTypography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </MDBox>

            <DialogActions>
                <MDButton onClick={onClose}>Cancel</MDButton>
                <MDButton onClick={handleConfirm} variant="contained" color="primary">
                    Confirm
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

SparePartsMultiSelectModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    value: PropTypes.array,
    onChange: PropTypes.func.isRequired,
};

export default SparePartsMultiSelectModal;
